import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Building, Download, MapPin } from 'lucide-react';

const BuildingIntelligenceDetail = () => {
  const { intelligenceId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntelligence();
  }, [intelligenceId]);

  const fetchIntelligence = async () => {
    try {
      const response = await axios.get(`${API}/building-intelligence/${intelligenceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntelligence(response.data);
    } catch (error) {
      toast.error('Failed to load building intelligence');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyTypeColor = (type) => {
    const colors = {
      'bank': 'bg-blue-100 text-blue-800',
      'office': 'bg-purple-100 text-purple-800',
      'school': 'bg-green-100 text-green-800',
      'retail': 'bg-orange-100 text-orange-800',
      'mixed_use': 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!intelligence) {
    return <div className="flex items-center justify-center py-12">Building intelligence not found</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/field/building-intelligence')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{intelligence.property_name}</h1>
            <div className="flex items-center text-gray-600 mb-2">
              <MapPin className="h-4 w-4 mr-2" />
              {intelligence.address}
            </div>
          </div>
          <Badge className={getPropertyTypeColor(intelligence.property_type)}>
            {intelligence.property_type.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Basic Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Property Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Date of Entry</p>
              <p className="font-medium">{_fmtDate(intelligence.date_of_entry)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Inspector</p>
              <p className="font-medium">{intelligence.inspector_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted</p>
              <p className="font-medium">{_fmtDateTime(intelligence.submitted_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Property Profile */}
      {intelligence.property_profile && Object.keys(intelligence.property_profile).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Property Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(intelligence.property_profile).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-medium">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contacts */}
      {intelligence.contacts && Object.keys(intelligence.contacts).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(intelligence.contacts).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-medium">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Access Control */}
      {intelligence.access_control && Object.keys(intelligence.access_control).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Access & Control</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(intelligence.access_control).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-medium">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exterior Intelligence */}
      {intelligence.exterior_intelligence && Object.keys(intelligence.exterior_intelligence).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Exterior Intelligence</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(intelligence.exterior_intelligence).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-medium">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interior Intelligence */}
      {intelligence.interior_intelligence && intelligence.interior_intelligence.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Interior Intelligence ({intelligence.interior_intelligence.length} Floors)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {intelligence.interior_intelligence.map((floor, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Floor {idx + 1}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {Object.entries(floor).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="ml-2">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lighting Intelligence */}
      {intelligence.lighting_intelligence && Object.keys(intelligence.lighting_intelligence).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Lighting Intelligence</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(intelligence.lighting_intelligence).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-medium">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Special Instructions */}
      {intelligence.special_instructions && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Special Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{intelligence.special_instructions}</p>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {intelligence.photos && intelligence.photos.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Photos ({intelligence.photos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {intelligence.photos.map((photo, idx) => (
                <div key={idx} className="border rounded-lg p-2">
                  <img src={photo} alt={`Building photo ${idx + 1}`} className="w-full h-40 object-cover rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={() => navigate('/field/building-intelligence')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button>
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>
    </div>
  );
};

export default BuildingIntelligenceDetail;
